import {Alert, Button, Card, Form, Input, InputNumber, Modal, Select, Switch, Table, Tabs, Typography} from "antd";
import {useEffect, useRef, useState} from "react";
import {t} from "i18next";
import {useHistory, useParams} from "react-router-dom";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import ApiService from "../../core/service/ApiService";
import FirmSelectList from "../firm/FirmSelectList";
import FileUtils from "../../core/utils/file-utils";
import Helper from "../../core/Helper";
import { TemplateLanguages } from "../../enums/TemplateLanguages";

export default function UnitCreateEdit({})
{
    const {unitId} = useParams();
    const [unit, setUnit] = useState({});
    const [visible, setVisible] = useState(false);
    const [form] = Form.useForm();
    const file = useRef(null);

    const history = useHistory();
    const formRef = useRef();

    const columns = [
        {
            title: t('ID'),
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: t('Form Adı'),
            dataIndex: 'name',
            key: 'name',
            // Yazdırma listesinde formu ayırt eden şey budur. Eski kayıtlarda boş olduğu için
            // yerinde düzenlenebilir; "Dil: X" öneki (eski migration) gösterilmeden kırpılır.
            render: (value, record) => (
                <Typography.Text
                    editable={{
                        tooltip: t('Adı düzenle'),
                        onChange: async (next) => {
                            await ApiService.Unit.RenameUnitForm(unitId, record.id, next)
                            await getUnit()
                        }
                    }}
                    type={value ? undefined : 'secondary'}
                >
                    {(value || '').replace(/^Dil:\s*/i, '').trim() || t('İsimsiz')}
                </Typography.Text>
            )
        },
        {
            title: t('Language'),
            dataIndex: 'language',
            key: 'language',
            // 3 karakterden uzunsa eski serbest metin; olduğu gibi gösterilir.
            render: (value) => (value && value.length <= 3 ? t(`lang_${value}`) : value) || '--'
        },
        {
            title: '#',
            key: 'actions',
            render: (_, record) => (
                <>
                    <Button type="link" onClick={() => handleDownload(record.id)}>{t('Download')}</Button>
                    <Button type="link" danger onClick={() => handleDelete(record.id)}>{t('Delete')}</Button>
                </>
            )
        }
    ];

    const handleDelete = async (id) => {
        const res = await ApiService.Unit.DeleteUnitForm(unitId, id);
        if(res.success) {
            await getUnit()
        }
    };

    const handleDownload = async (id) => {
        const res = await ApiService.Unit.GetDefaultForm(unitId, id);
        if(res.success) {
            Helper.File.saveFile({
                blobLink: res.data,
                fileName: res.fileName
            })
        }
    };

    const showModal = () => {
        setVisible(true);
    };

    const handleOk = async () => {
        await form.validateFields();
        const values = form.getFieldsValue();

        const res = await ApiService.Unit.UploadDefaultForm(unitId, {
            name: values.name,
            language: values.language,
            fileBase64: file.current.data,
        })

        if(res.success) {
            form.resetFields();
            setVisible(false);
            await getUnit()
        }
    };

    const handleCancel = () => {
        setVisible(false);
    };

    const onFinish = async (formData) => {
        if(unitId)
        {
            const res = await ApiService.Unit.EditUnit(unitId, {
                ...formData
            })
        }
        else
        {
            const res = await ApiService.Unit.CreateUnit({
                ...formData
            })

            if(res.success)
            {
                history.push(`/app/system/units/edit/${res.data}`)
            }
        }
    }

    const getUnit = async () => {
        if(unitId)
        {
            const res = await ApiService.Unit.GetUnit(unitId);
            if(res.success)
            {
                setUnit(res.data)
                formRef.current.setFieldsValue({
                    name: res.data.name,
                    visibleName: res.data.visibleName,
                    firmId: res.data.firmId,
                    hbysForeignKey: res.data.hbysForeignKey,
                    hbysFirmName: res.data.hbysFirmName,
                    hbysInstitutionName: res.data.hbysInstitutionName,
                    hbysInstitutionCode: res.data.hbysInstitutionCode,
                    hbysDoctor: res.data.hbysDoctor,
                    hl7Ip: res.data.hl7Ip,
                    hl7Port: res.data.hl7Port,
                    hl7TlsEnabled: res.data.hl7TlsEnabled,
                    hospitalTeletipId: res.data.hospitalTeletipId,
                    enabizEnabled: res.data.enabizEnabled,
                    enableNotification: res.data.enableNotification,
                    teletipUserName: res.data.teletipUserName,
                    teletipPassword: res.data.teletipPassword,
                    teletipApplicationCode: res.data.teletipApplicationCode,
                    medulaInstitutionId: res.data.medulaInstitutionId,
                    worklistEnabled: res.data.worklistEnabled,
                    worklistAeTitle: res.data.worklistAeTitle,
                    worklistModality: res.data.worklistModality,
                    worklistMergeStudyDescription: res.data.worklistMergeStudyDescription,
                    worklistHospitalName: res.data.worklistHospitalName,
                    worklistReferringPhysician: res.data.worklistReferringPhysician,
                    worklistPerformingPhysician: res.data.worklistPerformingPhysician,
                    worklistUidPrefix: res.data.worklistUidPrefix
                })
            }
        }
    }

    useEffect(() => {
        getUnit()
    }, [unitId])

    return (
        <SimpleLayout>
            <Card
                title={!unitId ? t('New Unit') : `${t('Edit Unit')} - ${unit.name}`}
            >
                {/* Form, Tabs'ın DIŞINDA: alanlar iki sekmeye yayılıyor ama tek Save hepsini
                    gönderiyor. Ayrı bir form olsaydı EditUnit'e kısmi veri giderdi ve orası her
                    alanı data'dan yazdığı için diğer sekmenin alanlarını null'lardı. */}
                <Form
                    ref={formRef}
                    layout={'vertical'}
                    onFinish={onFinish}
                >
                <Tabs>
                    <Tabs.TabPane tab={t('Unit')} key="unit" forceRender>
                            <Form.Item
                                label={t('Firm')}
                                name={'firmId'}
                                required={true}
                            >
                                <FirmSelectList />
                            </Form.Item>

                            <Form.Item
                                label={t('Name')}
                                name={'name'}
                                required={true}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t('Visible Name')}
                                name={'visibleName'}
                                tooltip={t('Shown in messages via the {UNIT} placeholder. If empty, Name is used.')}
                            >
                                <Input placeholder={t('If empty, Name is used.')} />
                            </Form.Item>

                            <Card title={t('HBYS Teletip Integration')} size="small" style={{ marginBottom: 16 }}>
                                <Form.Item
                                    label={t('HBYS Foreign Key')}
                                    name={'hbysForeignKey'}
                                >
                                    <Input placeholder={t('e.g. UNIT001')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('HBYS Firm Name')}
                                    name={'hbysFirmName'}
                                >
                                    <Input placeholder={t('e.g. HOSPITAL NAME')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('HBYS Institution Name')}
                                    name={'hbysInstitutionName'}
                                >
                                    <Input placeholder={t('e.g. RADIOLOGY DEPARTMENT')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('HBYS Institution Code')}
                                    name={'hbysInstitutionCode'}
                                >
                                    <Input placeholder={t('e.g. 123456')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('HBYS Doctor')}
                                    name={'hbysDoctor'}
                                >
                                    <Input placeholder={t('e.g. Dr. Name Surname')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('HL7 IP Address')}
                                    name={'hl7Ip'}
                                >
                                    <Input placeholder={t('e.g. 192.168.1.100')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('HL7 Port')}
                                    name={'hl7Port'}
                                >
                                    <InputNumber min={1} max={65535} placeholder={t('e.g. 2575')} style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    label={t('HL7 TLS Enabled')}
                                    name={'hl7TlsEnabled'}
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    label={t('Hospital Teletip ID')}
                                    name={'hospitalTeletipId'}
                                >
                                    <InputNumber min={1} placeholder={t('e.g. 1')} style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    label={t('E-Nabız Sending Status')}
                                    name={'enabizEnabled'}
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    label={t('Send Notifications')}
                                    name={'enableNotification'}
                                    valuePropName="checked"
                                    initialValue={true}
                                    tooltip={t('When off, no notifications are produced/sent for appointments in this unit.')}
                                >
                                    <Switch />
                                </Form.Item>

                                <Form.Item
                                    label={t('Teletıp Username')}
                                    name={'teletipUserName'}
                                >
                                    <Input placeholder={t('e.g. user@saglik.gov.tr')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('Teletıp Password')}
                                    name={'teletipPassword'}
                                >
                                    <Input.Password placeholder={t('Password')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('Teletip Application Code')}
                                    name={'teletipApplicationCode'}
                                >
                                    <Input placeholder={t('e.g. APP001')} />
                                </Form.Item>

                                <Form.Item
                                    label={t('Medula Institution ID')}
                                    name={'medulaInstitutionId'}
                                >
                                    <InputNumber min={1} placeholder={t('e.g. 123456')} style={{ width: '100%' }} />
                                </Form.Item>
                            </Card>
                    </Tabs.TabPane>

                    <Tabs.TabPane tab={t('DICOM Worklist')} key="worklist" forceRender>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={t('These settings are read by the worklist agent from HIS. Changes take effect within the agent\'s config refresh interval; no restart needed.')}
                        />

                        <Form.Item
                            label={t('Worklist Enabled')}
                            name={'worklistEnabled'}
                            valuePropName="checked"
                            tooltip={t('When off, appointments in this unit are never sent to the worklist.')}
                        >
                            <Switch />
                        </Form.Item>

                        <Form.Item
                            label={t('Device AE Title')}
                            name={'worklistAeTitle'}
                            tooltip={t('The modality\'s AE title. Used to match the querying device when AE filtering is on.')}
                        >
                            <Input maxLength={16} placeholder={t('e.g. AE1')} />
                        </Form.Item>

                        <Form.Item
                            label={t('Modality')}
                            name={'worklistModality'}
                            tooltip={t('DICOM modality (MR, CT, US ...). The exam room is derived from it: {Modality}{UnitId}.')}
                        >
                            <Input maxLength={16} placeholder={t('e.g. MR')} />
                        </Form.Item>

                        <Form.Item
                            label={t('Merge Study Description')}
                            name={'worklistMergeStudyDescription'}
                            valuePropName="checked"
                            tooltip={t('When on, all operation names of an appointment are merged into a single study description. Studies remain separate per operation.')}
                        >
                            <Switch />
                        </Form.Item>

                        <Form.Item
                            label={t('Hospital Name')}
                            name={'worklistHospitalName'}
                        >
                            <Input maxLength={255} />
                        </Form.Item>

                        <Form.Item
                            label={t('Referring Physician')}
                            name={'worklistReferringPhysician'}
                        >
                            <Input maxLength={255} />
                        </Form.Item>

                        <Form.Item
                            label={t('Performing Physician')}
                            name={'worklistPerformingPhysician'}
                        >
                            <Input maxLength={255} />
                        </Form.Item>

                        <Form.Item
                            label={t('UID Prefix (legacy)')}
                            name={'worklistUidPrefix'}
                            tooltip={t('No longer used for new studies: HIS now generates the StudyInstanceUID (2.25.<uuid>) and stores it on the operation. This prefix is only used to derive a UID for operations created before that field existed.')}
                        >
                            <Input maxLength={64} placeholder={t('e.g. 1.2.826.0.1.3680043.10.1693.01')} />
                        </Form.Item>
                    </Tabs.TabPane>

                    <Tabs.TabPane tab={t('Unit Forms')} key="unitForms">
                        <div>
                            <Button
                                type="primary"
                                onClick={showModal}
                                size={'small'}
                            >
                                Yeni Kayıt Ekle
                            </Button>
                            <Table
                                dataSource={unit.unitForms}
                                columns={columns}
                                rowKey="id"
                                size={'small'}
                                pagination={false}
                            />

                            <Modal
                                title={t('Add New Record')}
                                open={visible}
                                onOk={handleOk}
                                onCancel={handleCancel}
                                okText="Kaydet"
                                cancelText={t('Cancel')}
                            >
                                <Form form={form} layout="vertical" initialValues={{ language: 'tr' }}>
                                    {/* Ad ve dil AYRI alanlar. Önceden yalnız serbest metin "Language"
                                        vardı ve kullanıcılar formun adını oraya yazıyordu. */}
                                    <Form.Item name="name" label={t('Form Adı')} rules={[{ required: true, message: t('Form adı giriniz') }]}>
                                        <Input placeholder={t('ör. MR Onam Formu')} />
                                    </Form.Item>
                                    <Form.Item name="language" label={t('Language')} rules={[{ required: true, message: t('Please input a language!') }]}>
                                        <Select options={TemplateLanguages.map(code => ({ value: code, label: t(`lang_${code}`) }))} />
                                    </Form.Item>
                                    <Form.Item name="fileName" label={t('File')} rules={[{ required: true, message: t('Please select a file!') }]}>
                                        <Input disabled={true}/>
                                    </Form.Item>
                                    <Button
                                        size={'small'}
                                        onClick={async () => {
                                            file.current = await FileUtils.getFileAsBase64({})
                                            form.setFieldsValue({
                                                fileName: file.current.fileName,
                                            })
                                        }}
                                    >
                                        {t('Select File')}
                                    </Button>
                                </Form>
                            </Modal>
                        </div>
                    </Tabs.TabPane>
                </Tabs>

                {/* Save, Tabs'ın dışında: hangi sekmede olursan ol görünür ve tüm sekmelerin
                    alanlarını birlikte gönderir. */}
                <Form.Item style={{ marginTop: 16 }}>
                    <Button htmlType={'submit'} type={'primary'}>{t('Save')}</Button>
                </Form.Item>
                </Form>
            </Card>
        </SimpleLayout>
    )
}